import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { ArrowLeft, ArrowRight, PlusCircleFill } from 'react-bootstrap-icons';
import { toast } from 'sonner';
import { ABORT_ERROR, makeRESTApiRequest } from '../../../helpers/api';
import {
  clientStatusToOriginalOrderStatusMap,
  ENDPOINTS,
  ORDER_TYPES,
  SERVICES_RELATED_FILTERS,
  SERVICES_SORT_BY_OPTIONS,
  resolveServiceKeyValForOrderType
} from '../../../helpers/constants';
import { addOrUpdateObjectInArray, updatedServiceFiltereFields, isClient } from '../../../helpers/global';
import useAppChoices from '../../../hooks/useAppChoices';
import useQuery from '../../../hooks/useQueryParams';
import AlertModal from '../../AlertModal';
import FloatingButton from '../../FloatingButton';
import SlidingSideBar from '../../SlidingSideBar/SlidingSideBar';
import AddEditServiceSidebar from './AddEditServiceSidebar';
import FilterServices from './FilterServices';
import LoadingServiceCard from './LoadingServiceCard';
import ServiceAdditionalFilters from './ServiceAdditionalFilters';
import ServiceCard from './ServiceCard';
import { orderStatusForClientMap, getStartDateAndEndDateByRange } from '../../../helpers/constants';
import AddEditReportSideBar from './lien-search/AddEditReportSideBar';
import AddEditSurveyReportSidebar from './survey/AddEditSurveyReportSidebar';
import AddEditEstoppelReportSidebar from './estoppel/AddEditEstoppelReportSidebar';
import { getFullAddress } from '../../../helpers/global';
import { useHistory } from 'react-router-dom';
// Return common array of objects based on field
function getCommonElementsFromArrays(arrayOfArrays, field) {
  if (!arrayOfArrays || arrayOfArrays.length === 0) {
    return [];
  }
  // Use the first array as the reference for common elements
  let commonElements = arrayOfArrays[0];

  for (let i = 1; i < arrayOfArrays.length; i++) {
    const currentArray = arrayOfArrays[i];
    const nextCommonElements = [];

    for (const commonEl of commonElements) {
      if (commonEl.hasOwnProperty(field)) {
        const found = currentArray.some(
          currentEl => currentEl.hasOwnProperty(field) && currentEl[field] === commonEl[field]
        );
        if (found) {
          nextCommonElements.push(commonEl);
        }
      }
    }
    commonElements = nextCommonElements;
  }

  // Remove duplicates from the final common elements (in case the input arrays had duplicates)
  const uniqueCommonElements = [];
  const seenValues = new Set();

  for (const element of commonElements) {
    if (!seenValues.has(element[field])) {
      uniqueCommonElements.push(element);
      seenValues.add(element[field]);
    }
  }
  return uniqueCommonElements;
}

const isSideBarFilterApplied = (serviceFilters, orderFilters) => {
  const orgApplied = Boolean(
    serviceFilters.organization && serviceFilters.organization.$in && serviceFilters.organization.$in.length > 0
  );
  const closingDateApplied = Boolean(
    serviceFilters.closingDate && serviceFilters.closingDate.$gte && serviceFilters.closingDate.$lte
  );
  const processorsApplied = Boolean(
    orderFilters['orders.assignedProcessors'] &&
      orderFilters['orders.assignedProcessors'].$in &&
      orderFilters['orders.assignedProcessors'].$in.length > 0
  );
  const createdAtApplied = Boolean(
    orderFilters['orders.createdAt'] && orderFilters['orders.createdAt'].$gte && orderFilters['orders.createdAt'].$lte
  );
  const dateCancelledApplied = Boolean(
    orderFilters['orders.dateCancelled'] &&
      orderFilters['orders.dateCancelled'].$gt &&
      orderFilters['orders.dateCancelled'].$lt
  );
  const dateDeliveredApplied = Boolean(
    orderFilters['orders.dateDelivered'] &&
      orderFilters['orders.dateDelivered'].$gte &&
      orderFilters['orders.dateDelivered'].$lte
  );

  const rushApplied = Boolean(
    orderFilters['orders.rush'] &&
      Array.isArray(orderFilters['orders.rush'].$in) &&
      orderFilters['orders.rush'].$in.length > 0
  );

  return (
    orgApplied ||
    processorsApplied ||
    createdAtApplied ||
    dateCancelledApplied ||
    dateDeliveredApplied ||
    closingDateApplied ||
    rushApplied
  );
};

// provide labels statuses object
const getLabelStatus = statuses => {
  return statuses.map(value => {
    return {
      clientLable: orderStatusForClientMap[value] || value,
      value: value
    };
  });
};

const updateEnabled = (obj1, obj2) => {
  let result = {};
  for (let key in obj1) {
    if (obj2.hasOwnProperty(key)) {
      result[key] = { ...obj1[key], enabled: obj2[key].enabled };
    } else {
      result[key] = obj1[key];
    }
  }
  return result;
};

const getDefaultOrderAppliedStatuses = ({ additionalOptions, appliedOptions, role, orderStatus, field }) => {
  const allDisabled = () =>
    appliedOptions.reduce((acc, filter) => ({ ...acc, [filter.key]: { values: [], enabled: false } }), {});

  if (!orderStatus) return allDisabled();

  // Handle both single value and array of values
  const orderStatusArray = Array.isArray(orderStatus) ? orderStatus : [orderStatus];

  const orderTypesWithThisStatus = additionalOptions
    .filter(o => orderStatusArray.some(status => o.options.some(option => option.value === status)))
    .map(o => o.key);

  if (orderTypesWithThisStatus.length === 0) {
    return allDisabled();
  }

  if (orderTypesWithThisStatus.length > 1) {
    const includeCommon = [...orderTypesWithThisStatus, 'Common'];

    return {
      ...appliedOptions.reduce(
        (acc, filter) => ({
          ...acc,
          [filter.key]: {
            values:
              field === 'clientLable'
                ? orderStatusArray
                    .filter(status => filter.options.some(option => option.value === status))
                    .map(status => orderStatusForClientMap[status])
                    .map(status => clientStatusToOriginalOrderStatusMap[status])
                    .flat()
                : orderStatusArray.filter(status => filter.options.some(option => option.value === status)),
            enabled: includeCommon.includes(filter.key)
          }
        }),
        {}
      )
    };
  }

  return additionalOptions.reduce(
    (acc, filter) => ({
      ...acc,
      [filter.key]: {
        values:
          field === 'clientLable'
            ? orderStatusArray
                .filter(status => filter.options.some(option => option.value === status))
                .map(status => orderStatusForClientMap[status])
                .map(status => clientStatusToOriginalOrderStatusMap[status])
                .flat()
            : orderStatusArray.filter(status => filter.options.some(option => option.value === status)),
        enabled: orderTypesWithThisStatus.includes(filter.key)
      }
    }),
    {}
  );
};

const getDefaultOrderOptionStatuses = ({ additionalOptions, role, orderStatus, field }) => {
  if (!orderStatus) return additionalOptions;

  // Handle both single value and array of values
  const orderStatusArray = Array.isArray(orderStatus) ? orderStatus : [orderStatus];
  const orderTypesWithThisStatus = additionalOptions
    .filter(o => orderStatusArray.some(status => o.options.some(option => option.value === status)))
    .map(o => o.key);

  if (orderTypesWithThisStatus.length < 2) {
    return additionalOptions;
  }

  const commonFilters = getCommonElementsFromArrays(
    additionalOptions.filter(o => orderTypesWithThisStatus.includes(o.key)).map(o => o.options),
    field
  );
  const newFilterOption = {
    label: 'Common',
    key: 'Common',
    options: commonFilters
  };

  const additionalFiltersOptions = additionalOptions.map(a => {
    if (orderTypesWithThisStatus.includes(a.key)) {
      return {
        ...a,
        options: a.options.filter(o => !commonFilters.some(cf => cf[field] === o[field]))
      };
    } else {
      return a;
    }
  });

  return [newFilterOption, ...additionalFiltersOptions];
};

const ServicesPipeline = ({ serviceDetails = false, match, createdService, onServiceHandled }) => {
  const user = JSON.parse(localStorage.getItem('user')) || {};
  const [role] = useState(localStorage.getItem('user-role'));
  // This use effect is trigger only for the first time when the filtermetadata is updated
  const [triggerFetchUseEffect, setTriggerFetchUseEffect] = useState(false);
  const users = useAppChoices('users');
  const organizations = useAppChoices('organizations');
  const lienSearchStatuses = useAppChoices('Lien Search Status');
  const estoppelStatuses = useAppChoices('Estoppel Status');
  const surveyStatuses = useAppChoices('Survey Status');

  const [loadingServices, setLoadingServices] = useState(true);
  const [services, setServices] = useState([]);
  const [activeService, setActiveService] = useState();
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);
  const [filterMetadata, setShowFilterMetadata] = useState(
    updatedServiceFiltereFields(SERVICES_RELATED_FILTERS(users, organizations), user)
  );
  const [filteredServices, setFilteredServices] = useState([]);
  const [serviceSortOption, setServiceSortOption] = useState(SERVICES_SORT_BY_OPTIONS[0]);
  const [SideBarFilterApplied, setSideBarFilterApplied] = useState(false);

  const [reportSidebarMeta, setReportSidebarMeta] = useState(null);
  const { orderStatus, organizationId, range, orderId, serviceId, OrderType, reportType } = useQuery();
  const history = useHistory();

  const serviceKeyValWithOrderType = useMemo(
    () => resolveServiceKeyValForOrderType(OrderType || reportType),
    [OrderType, reportType]
  );

  useEffect(() => {
    if (createdService) {
      setServices(prevServices => [createdService, ...prevServices]);
      setActiveService(createdService);
      onServiceHandled && onServiceHandled();
    }
  }, [createdService]);

  // side effect to update filter metadata when organizationId is present in URL
  useEffect(() => {
    let updatedMetadata = [...filterMetadata];
    if (organizationId) {
      let orgIds = [];
      Array.isArray(organizationId) ? (orgIds = organizationId) : (orgIds = [organizationId]);

      updatedMetadata = filterMetadata.map(filter => {
        if (filter.key === 'organization') {
          return {
            ...filter,
            values: orgIds
          };
        }
        return filter;
      });
    }

    if (range && orderStatus) {
      const { startDate, endDate } = getStartDateAndEndDateByRange(range);

      if (orderStatus === 'Pending To Pay') {
        const dateKey =
          range === 'PAST_CLOSING_DATE' || range === 'AWAITING_CLOSING' ? 'closingDate' : 'dateDelivered';
        updatedMetadata = updatedMetadata.map(f => {
          if (f.key === dateKey) {
            return { ...f, values: { startDate, endDate } };
          }
          return f;
        });
      } else if (
        orderStatus.includes('Cancelled') ||
        orderStatus.includes('Refunded') ||
        orderStatus.includes('Refund Requested')
      ) {
        updatedMetadata = updatedMetadata.map(f => {
          if (f.key === 'dateCancelled') {
            return { ...f, values: { startDate, endDate } };
          }
          return f;
        });
      }
    }

    setTriggerFetchUseEffect(true);
    setShowFilterMetadata(updatedMetadata);
  }, []);

  const additionalOptions = useMemo(
    () => [
      { label: 'Lien Search', key: 'Lien Search', options: getLabelStatus(lienSearchStatuses) },
      { label: 'Estoppel', key: 'Estoppel', options: getLabelStatus(estoppelStatuses) },
      { label: 'Survey', key: 'Survey', options: getLabelStatus(surveyStatuses) }
    ],
    [lienSearchStatuses, estoppelStatuses, surveyStatuses]
  );

  const additionalFilters = useMemo(
    () =>
      additionalOptions.reduce(
        (acc, filter) => ({
          ...acc,
          [filter.key]: { values: [...filter.options], enabled: false }
        }),
        {}
      ),
    [additionalOptions]
  );

  const [appliedOptions, setAppliedOptions] = useState(
    getDefaultOrderOptionStatuses({
      additionalOptions,
      role,
      orderStatus,
      field: isClient(role) ? 'clientLable' : 'value'
    })
  );

  const [appliedFilters, setAppliedFilters] = useState(
    getDefaultOrderAppliedStatuses({
      additionalOptions,
      appliedOptions,
      role,
      orderStatus,
      field: isClient(role) ? 'clientLable' : 'value'
    })
  );
  const [fullScreenMode, setFullScreenMode] = useState(serviceDetails);

  const [addUpdateServiceMeta, setAddUpdateServiceMeta] = useState(null);
  const [deleteModalMeta, setDeleteModalMeta] = useState(null);
  const abortControllerRef = useRef(null);

  //pipeline filter related

  const onFilterMetadataChange = value => {
    setShowFilterMetadata(value);
  };

  const onServiceSortOptionChange = sortOption => {
    setServiceSortOption(sortOption);
  };

  const onShowFilterSidebarChange = value => {
    setShowFilterSidebar(value);
  };

  useEffect(() => {
    if (triggerFetchUseEffect) {
      fetchServices();
    }
  }, [serviceSortOption, appliedFilters, triggerFetchUseEffect]);

  useEffect(() => {
    if (!activeService) return;
    //auto details fetching is done only once for service.
    //if there is an error, customer clicks on reload button, which calls fetchCompleteDetails
    if (!['orders', 'loadingError'].some(k => activeService.hasOwnProperty(k))) fetchCompleteDetails();
  }, [activeService]);

  /** On clicks */

  const onServiceDeleteClick = () => {
    setDeleteModalMeta({ toBeDeletedService: activeService });
  };

  /** On clicks */

  const onServiceEditClick = () => {
    setAddUpdateServiceMeta({ ...activeService });
  };

  /** State Change functions */

  const onServiceAddUpdate = service => {
    setServices(addOrUpdateObjectInArray(services, service));
    setActiveService(service);
  };

  const onAdditionalFilterValuesChange = (key, option) => {
    const optionIsArray = Array.isArray(option);
    const existingFilters = appliedFilters[key].values;

    const optionMatchWithExisting = optionIsArray
      ? option.some(ex => existingFilters.includes(ex))
      : existingFilters.includes(option);

    if (optionMatchWithExisting) {
      appliedFilters[key].values = appliedFilters[key].values.filter(o =>
        !optionIsArray ? o !== option : !option.includes(o)
      );
    } else {
      if (optionIsArray) {
        appliedFilters[key].values.push(...option);
      } else {
        appliedFilters[key].values.push(option);
      }
    }
    setAppliedFilters({ ...appliedFilters });
  };

  // function updates state of  appliedValues  when enabled is less then one or updates adding a common field between enables object values  when enabled is more than one
  const onEnableAdditionalFilterCheckChange = key => {
    const filterBy = isClient(role) ? 'clientLable' : 'value';
    const updatedFilters = {
      ...updateEnabled(additionalFilters, appliedFilters),
      [key]: { ...additionalFilters[key], enabled: !appliedFilters[key].enabled }
    };

    const enabledFilters = Object.values(updatedFilters).filter(f => f.enabled && f?.key !== 'Common');
    const enabledFilterValues = enabledFilters.map(f => f.values);
    const commonFilters = getCommonElementsFromArrays(enabledFilterValues, filterBy);
    const enabledSelectedFilter = Object.values(appliedFilters).filter(f => f.enabled);
    const enabledSelectedCommonFilter = [...new Set(enabledSelectedFilter.map(f => f.values).flat())];

    if (enabledFilters.length > 1) {
      const newFilterOption = {
        label: 'Common',
        key: 'Common',
        options: commonFilters
      };
      const additionalFiltersOptions = additionalOptions.map(a => {
        if (
          Object.keys(updatedFilters)
            .filter(key => updatedFilters[key].enabled)
            .includes(a.key)
        ) {
          return {
            ...a,
            options: a.options.filter(o => !commonFilters.some(cf => cf[filterBy] === o[filterBy]))
          };
        } else {
          return a;
        }
      });

      const filteredAppliedFilters = Object.keys(updatedFilters)
        .filter(key => key !== 'Common')
        .map(key => {
          if (!updatedFilters[key].enabled) {
            return {
              [key]: {
                ...updatedFilters[key]
              }
            };
          } else {
            return {
              [key]: {
                ...updatedFilters[key],
                values: []
              }
            };
          }
        });

      setAppliedFilters({
        ...filteredAppliedFilters.reduce((obj, item) => {
          const key = Object.keys(item)[0];
          obj[key] = item[key];
          return obj;
        }, {}),
        [newFilterOption.key]: {
          values: newFilterOption.options.filter(f =>
            enabledSelectedCommonFilter.some(ef => ef[filterBy] === f[filterBy])
          ),
          enabled: true,
          key: 'Common'
        }
      });
      setAppliedOptions([newFilterOption, ...additionalFiltersOptions]);
    } else {
      setAppliedOptions([...additionalOptions]);
      setAppliedFilters(
        additionalOptions.reduce(
          (acc, filter) => ({
            ...acc,
            [filter.key]: {
              values: [],
              enabled: updatedFilters[filter.key].enabled
            }
          }),
          {}
        )
      );
    }
  };

  const onAllAdditionalFilterCheckChange = key => {
    const existingFilters = appliedFilters[key].values;
    const options = appliedOptions.find(f => f.key === key).options;
    if (existingFilters.length === options.length) {
      appliedFilters[key].values = [];
    } else {
      appliedFilters[key].values = [...options.map(o => o.value)];
    }
    setAppliedFilters({ ...appliedFilters });
  };

  const getServiceAndOrderFilters = () => {
    const serviceFilters = serviceDetails ? { _id: match?.params?.id } : {};

    const commonOrderStatuses = appliedFilters.Common?.values || [];

    const enabledOrderTypes = Object.keys(appliedFilters).filter(
      type => type !== 'Common' && appliedFilters[type].enabled
    );

    const orderFilters = !serviceDetails
      ? enabledOrderTypes.length > 0
        ? {
            $or: enabledOrderTypes.map(type => {
              const statuses = [...appliedFilters[type].values, ...commonOrderStatuses];
              return {
                'orders.orderType': type,
                'orders.status': statuses.length > 0 ? { $in: statuses } : undefined
              };
            })
          }
        : {}
      : {};

    // Process order-related filters (date and checkbox)
    filterMetadata
      .filter(
        f =>
          f.orderRelatedFilter &&
          f.values &&
          ((f.type === 'date' && f.values.startDate && f.values.endDate) ||
            (f.type === 'checkbox' && Array.isArray(f.values) && f.values.length > 0))
      )
      .forEach(f => {
        if (f.type === 'date') {
          if (f.key === 'dateCancelled') {
            orderFilters[`orders.dateCancelled`] = {
              $gt: f.values.startDate,
              $lt: f.values.endDate
            };
          } else {
            orderFilters[`orders.${f.key}`] = {
              $gte: f.values.startDate,
              $lte: f.values.endDate
            };
          }
        } else if (f.type === 'checkbox') {
          if (f.key === 'cancellationRequested' && f.values.length === 1 && f.values[0] === false) {
            orderFilters[`orders.${f.key}`] = { $ne: true };
          } else {
            orderFilters[`orders.${f.key}`] = { $in: f.values };
          }
        }
      });

    // Process service-related filters (checkbox and date)
    filterMetadata
      .filter(
        f =>
          !f.orderRelatedFilter &&
          f.values &&
          ((f.type === 'date' && f.values.startDate && f.values.endDate) ||
            (f.type === 'checkbox' && Array.isArray(f.values) && f.values.length > 0))
      )
      .forEach(f => {
        if (f.type === 'date') {
          serviceFilters[f.key] = {
            $gte: f.values.startDate,
            $lte: f.values.endDate
          };
        } else if (f.type === 'checkbox') {
          serviceFilters[f.key] = { $in: f.values };
        }
      });

    setSideBarFilterApplied(isSideBarFilterApplied(serviceFilters, orderFilters));

    return {
      serviceFilters,
      orderFilters
    };
  };

  /** Api calls */
  const fetchServices = async () => {
    // If an old API call is in progress, abort it
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setLoadingServices(true);

    const controller = new AbortController();
    const { signal } = controller;

    abortControllerRef.current = controller;

    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.FETCH_SERVICES,
      requestBody: {
        filter: getServiceAndOrderFilters(),
        sort: {
          [serviceSortOption.key]: serviceSortOption.direction || -1
        },
        compactMode: true
      },
      signal
    });

    if (error !== ABORT_ERROR) {
      setLoadingServices(false);

      if (error) {
        toast.error(error);
        return;
      }
      setServices(response);
      setActiveService(serviceId ? response.find(s => s._id === serviceId) : response[0]);
    }
  };

  const fetchCompleteDetails = async () => {
    onServiceAddUpdate({ ...activeService, loadingError: false });

    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.FETCH_SERVICES,
      requestBody: {
        filter: { serviceFilters: { _id: activeService?._id } }
      }
    });

    if (error) {
      onServiceAddUpdate({ ...activeService, loadingError: true });
      return;
    }

    response[0] && onServiceAddUpdate(response[0]);
  };

  useEffect(() => {
    const isEstoppel = reportType === ORDER_TYPES.ESTOPPEL;
    const isSurvey = reportType === ORDER_TYPES.SURVEY;
    const reportSidebarType = isSurvey ? 'survey' : isEstoppel ? 'estoppel' : 'lien-search';

    const fetchReportSideBarMetaData = async () => {
      setReportSidebarMeta({ show: true, loading: true, type: reportSidebarType });

      const reportEndpoint = isSurvey
        ? ENDPOINTS.SURVEY_REPORT_BASE
        : isEstoppel
          ? ENDPOINTS.ESTOPPEL_REPORT_BASE
          : ENDPOINTS.REPORT_BASE;

      const [servicesResult, reportResult] = await Promise.all([
        makeRESTApiRequest({
          endpoint: ENDPOINTS.FETCH_SERVICES,
          requestBody: {
            filter: { serviceFilters: { _id: serviceId } }
          }
        }),
        makeRESTApiRequest({
          endpoint: reportEndpoint,
          method: 'POST',
          requestBody: { orderId }
        })
      ]);

      const { response: servicesResponse, error: servicesError } = servicesResult;
      const { response: reportResponse, error: reportError } = reportResult;

      if (servicesError || reportError) {
        console.error('API Error:', { servicesError, reportError });
        setReportSidebarMeta({ show: false, loading: false, type: reportSidebarType });
        return;
      }

      const baseMeta = {
        show: true,
        service: servicesResponse[0],
        loading: false,
        reportId: reportResponse._id
      };

      if (isSurvey) {
        setReportSidebarMeta({
          ...baseMeta,
          type: 'survey',
          survey: {
            ...servicesResponse[0],
            report: reportResponse,
            reportId: reportResponse._id
          }
        });
      } else if (isEstoppel) {
        setReportSidebarMeta({
          ...baseMeta,
          type: 'estoppel',
          estoppel: {
            ...servicesResponse,
            report: {
              ...reportResponse,
              propertyAddress: getFullAddress(
                Array.isArray(servicesResponse) ? servicesResponse[0] : servicesResponse
              ),
              owner: servicesResponse?.sellerName || servicesResponse?.[0]?.sellerName
            },
            reportId: reportResponse._id
          }
        });
      } else {
        setReportSidebarMeta({
          ...baseMeta,
          type: 'lien-search',
          lienSearch: {
            ...servicesResponse,
            report: {
              ...reportResponse,
              propertyAddress: getFullAddress(Array.isArray(servicesResponse) ? servicesResponse[0] : servicesResponse),
              owner: servicesResponse?.sellerName || servicesResponse?.[0]?.sellerName
            },
            reportId: reportResponse._id
          }
        });
      }
    };

    if (orderId && serviceId) {
      fetchReportSideBarMetaData();
    }
  }, [orderId, serviceId, reportType]);

  const deleteService = async () => {
    const { toBeDeletedService } = deleteModalMeta;
    setDeleteModalMeta({ ...deleteModalMeta, deletingService: true });
    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.SERVICE_WITH_ID(toBeDeletedService._id),
        method: 'DELETE'
      });
      if (error) {
        toast.error(error);
        return;
      }
      setServices(services.filter(s => s._id !== toBeDeletedService._id));
      setDeleteModalMeta(null);
      toast.success('Service deleted successfully');
      history.push(`/services/pipeline`);
    } catch (e) {
      toast.error(e);
    } finally {
      setDeleteModalMeta(null);
    }
  };

  const onFilterServices = () => {
    fetchServices();
    onShowFilterSidebarChange(false);
  };

  return (
    <>
      <SlidingSideBar
        style={{ zIndex: '99999999', width: 450, left: 'unset', right: 0 }}
        visible={showFilterSidebar}
        onClose={() => onShowFilterSidebarChange(false)}
        title="Filter"
      >
        <FilterServices
          filterMetadata={filterMetadata}
          onFilterMetadataChange={onFilterMetadataChange}
          onFilterServices={onFilterServices}
          initialOptions={filterMetadata}
        />
      </SlidingSideBar>

      <Row className="m-0" style={{ height: 'calc(100vh - 64px)' }}>
        <Col
          xs={fullScreenMode ? 12 : 8}
          md={fullScreenMode ? 12 : 10}
          className="h-100 px-2"
          style={{ overflowY: 'auto' }}
        >
          {!fullScreenMode && (
            <ServiceAdditionalFilters
              isSideBarFilterApplied={SideBarFilterApplied}
              filterOptions={appliedOptions}
              filterValues={appliedFilters}
              onFilterValueChange={onAdditionalFilterValuesChange}
              onEnableFilterCheckChange={onEnableAdditionalFilterCheckChange}
              onAllFilterCheckChange={onAllAdditionalFilterCheckChange}
              onShowFilterSidebarChange={onShowFilterSidebarChange}
              serviceSortOption={serviceSortOption}
              onServiceSortOptionChange={onServiceSortOptionChange}
            />
          )}
          {activeService?.orders ? (
            <ServiceCard
              service={activeService}
              onServiceUpdate={onServiceAddUpdate}
              onServiceDeleteClick={onServiceDeleteClick}
              onServiceEditClick={onServiceEditClick}
              onShowFilterSidebarChange={onShowFilterSidebarChange}
              onServiceSortOptionChange={onServiceSortOptionChange}
              serviceKeyValWithOrderType={serviceKeyValWithOrderType}
            />
          ) : loadingServices || activeService ? (
            <LoadingServiceCard service={activeService} onReloadClick={fetchCompleteDetails} />
          ) : (
            <Card className="mt-2 mb-5">
              <div className="p-5 largeFont text-muted text-center">No service selected!</div>
            </Card>
          )}
        </Col>
      </Row>
      <AlertModal
        show={deleteModalMeta !== null}
        onHide={() => setDeleteModalMeta(null)}
        onDismissClick={() => setDeleteModalMeta(null)}
        onContinueClick={deleteService}
        showProgress={deleteModalMeta?.deletingService}
        alertText={`Are you sure you want to delete this service "${deleteModalMeta?.toBeDeletedService.fileName ||
          ''}"? This action cannot be undone!`}
        progressText="Deleting service..."
      />

      {!loadingServices && !serviceDetails && (
        <>
          {services.length > 0 && (
            <FloatingButton
              variant="primary"
              verticalAlign="bottom"
              horizontalAlign="left"
              text={fullScreenMode ? 'Expand' : 'Collapse'}
              icon={
                fullScreenMode ? (
                  <ArrowRight className="mr-2 align-text-top" size={18} />
                ) : (
                  <ArrowLeft className="mr-2 align-text-top" size={18} />
                )
              }
              onClick={() => setFullScreenMode(!fullScreenMode)}
            />
          )}
        </>
      )}

      <AddEditReportSideBar
        loading={reportSidebarMeta?.loading}
        show={reportSidebarMeta?.show && reportSidebarMeta?.type === 'lien-search'}
        lienSearch={reportSidebarMeta?.lienSearch}
        onAfterSave={fetchCompleteDetails}
        onHide={() => setReportSidebarMeta(null)}
        service={reportSidebarMeta?.service}
      />

      <AddEditEstoppelReportSidebar
        loading={reportSidebarMeta?.loading}
        show={reportSidebarMeta?.show && reportSidebarMeta?.type === 'estoppel'}
        estoppel={reportSidebarMeta?.estoppel}
        onAfterSave={fetchCompleteDetails}
        onOrderHoaUpdated={fetchCompleteDetails}
        onHide={() => setReportSidebarMeta(null)}
        service={reportSidebarMeta?.service}
        estoppelOrderId={orderId}
      />

      <AddEditSurveyReportSidebar
        loading={reportSidebarMeta?.loading}
        show={reportSidebarMeta?.show && reportSidebarMeta?.type === 'survey'}
        survey={reportSidebarMeta?.survey}
        onAfterSave={fetchCompleteDetails}
        onHide={() => setReportSidebarMeta(null)}
        service={reportSidebarMeta?.service}
        surveyOrderId={orderId}
      />

      <AddEditServiceSidebar
        onServiceAddUpdate={onServiceAddUpdate}
        show={addUpdateServiceMeta !== null}
        editingService={addUpdateServiceMeta}
        onHide={() => setAddUpdateServiceMeta(null)}
      />

    </>
  );
};

export default ServicesPipeline;
